import React, { useState, useRef, useEffect } from "react";
import Modal from "../Modal"; // Import the reusable modal
import { format, differenceInDays, parseISO } from "date-fns";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FlightIcon from "@mui/icons-material/Flight";
import HotelIcon from "@mui/icons-material/Hotel";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import StarIcon from "@mui/icons-material/Star";
import DirectionsMapComp from "../../components/DirectionsMap";
import { axios } from "../../utils/axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const TripModal = ({ selectedTrip, open, handleClose }) => {
  const navigate = useNavigate();
  const mapHeight = useRef();
  const [openModel, setOpenModel] = useState(false);
  const [selectedDay, setSelectedDay] = useState(0);
  const dayRefs = useRef([]); // Initialize as an empty array

  const addToTrip = () => {
    axios
      .post(process.env.REACT_APP_BACKEND + "clone/clonetrip", {
        tripId: selectedTrip._id,
      })
      .then((resp) => {
        toast.success(resp?.data?.message);
        handleClose();
      })
      .catch((err) => {
        console.log(err);
        toast.error("Please login to add the trip");
        handleClose();
        setOpenModel(true);
      });
  };

  // Get the pins for the selected day from tripItinerary and match them with tripPins
  const filteredPins = () => {
    const itineraryForDay =
      selectedTrip?.itinerary[selectedDay]?.tripItinerary || [];

    const pinsInOrder = itineraryForDay
      .filter((item) => item.type === "pin")
      .map((item, index) => ({
        ...item,
        number: index + 1,
      }));

    const matchedPins = pinsInOrder.map((pin) => {
      const fullPinDetails = selectedTrip?.tripPins.find(
        (tripPin) => tripPin._id === pin._id
      );
      return {
        ...fullPinDetails,
        number: pin.number,
      };
    });

    return matchedPins;
  };

  // Set up IntersectionObserver to detect when a day section enters the viewport
  useEffect(() => {
    // Reset dayRefs.current when selectedTrip?.itinerary changes
    dayRefs.current = Array(selectedTrip?.itinerary?.length || 0).fill(null);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const dayIndex = parseInt(
              entry.target.getAttribute("data-day"),
              10
            );
            console.log("Day in view:", dayIndex); // Debugging
            setSelectedDay(dayIndex);
          }
        });
      },
      {
        root: mapHeight.current,
        rootMargin: "-40% 0px -40% 0px",
        threshold: 0.1,
      }
    );

    // Function to observe refs
    const observeRefs = () => {
      console.log("Observing refs:", dayRefs.current); // Debugging
      observer.disconnect(); // Clear previous observations
      dayRefs.current.forEach((ref, index) => {
        if (ref) {
          observer.observe(ref);
        } else {
          console.warn(`Ref for day ${index} is null`); // Debugging
        }
      });
    };

    // Delay observation to ensure refs are populated
    const timer = setTimeout(() => {
      observeRefs();
    }, 0);

    // Cleanup observer on unmount
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [selectedTrip?.itinerary]); // Re-run when selectedTrip?.itinerary changes

  return (
    <>
      <Modal open={open} onClose={handleClose} size="xl">
        <div
          className="row bxShadow"
          style={{
            backgroundColor: "var(--white-color)",
            borderRadius: "6px",
          }}
        >
          <div
            className="col-lg-3 bgsearch col-sm-4 p-4 m-0"
            style={{
              backgroundColor: "var(--white-color)",
              borderTopLeftRadius: "6px",
              borderBottomLeftRadius: "6px",
            }}
          >
            <h2 className="my-3">{selectedTrip?.name}</h2>
            <div className="d-flex align-items-center">
              {selectedTrip && (
                <p className="ms-2">
                  Duration:{" "}
                  {differenceInDays(
                    parseISO(selectedTrip?.endDate),
                    parseISO(selectedTrip?.startDate)
                  ) + 1}{" "}
                  days,{" "}
                  {differenceInDays(
                    parseISO(selectedTrip?.endDate),
                    parseISO(selectedTrip?.startDate)
                  )}{" "}
                  nights
                </p>
              )}
            </div>
            <div className="d-flex align-items-center mt-3">
              <LocationOnIcon />
              <p className="ms-1">{selectedTrip?.location}</p>
            </div>
            <button className="btn-contained mt-4" onClick={addToTrip}>
              Add Trip
            </button>
          </div>
          <div
            className="col-lg-9 col-sm-8 p-0 m-0 bgImageTrip"
            style={{ backgroundImage: `url(${selectedTrip?.image})` }}
          ></div>
        </div>
        <div className="row" style={{ height: "88vh" }}>
          <div
            className="col-md-6"
            ref={mapHeight}
            style={{ overflowY: "auto", height: "100%", padding: "15px" }}
          >
            {selectedTrip?.itinerary.map((sti, index) => (
              <div
                key={sti._id}
                ref={(el) => (dayRefs.current[index] = el)} // Assign ref to each day section
                data-day={index} // Add data attribute to identify the day
                className="row bxShadow mt-3"
                style={{
                  background: "var(--white-color)",
                  borderRadius: "4px",
                  position: "relative",
                  minHeight: "8rem",
                }}
              >
                <div className="col-md-12 p-4 ps-0">
                  <div
                    className=""
                    style={{
                      position: "absolute",
                      zIndex: "11",
                      background:
                        selectedDay === index
                          ? "var(--primary-color)"
                          : "var(--yellow-color)",
                      top: "38%",
                      textAlign: "center",
                      color: "var(--white-color)",
                      padding: "8px 10px",
                      borderTopRightRadius: "6px",
                      borderBottomRightRadius: "6px",
                    }}
                    onClick={() => setSelectedDay(index)}
                  >
                    <p className="fw-700">Day</p>
                    <p className="m-0 p-0 fw-700">{index + 1}</p>
                  </div>
                  {sti.tripItinerary.map((item, index) => (
                    <div
                      key={index}
                      className="policy-item"
                      style={{ marginLeft: "60px" }}
                    >
                      <div className="policy-icon">
                        {item?.type === "flight" && (
                          <FlightIcon
                            style={{ color: "var(--primary-color)" }}
                          />
                        )}
                        {item?.type === "hotel" && (
                          <HotelIcon
                            style={{ color: "var(--primary-color)" }}
                          />
                        )}
                        {item?.type === "pin" && (
                          <LocationOnIcon
                            style={{ color: "var(--primary-color)" }}
                          />
                        )}
                      </div>

                      {item?.type === "flight" && (
                        <div className="d-flex align-items-start pe-2">
                          <div style={{ width: "15%", height: "15%" }}>
                            <img
                              src={item?.imgs}
                              width="100%"
                              style={{ objectFit: "contain" }}
                              className="border-radius-6px imgclass"
                            />
                          </div>
                          <div className="w100 ms-3">
                            <div className="d-flex align-items-center">
                              <h3 className="me-3">{item?.arrival.location}</h3>
                              <FlightTakeoffIcon />
                              <h3 className="ms-3">
                                {item?.departure.location}
                              </h3>
                            </div>
                            <div className="d-flex align-items-center justify-content-between mt-2 df-wrap">
                              <p className="fs-8">{item?.airport}</p>
                              <p className="fs-8 ms-2">
                                {item.arrival.date}{" "}
                                <span className="ms-2">
                                  {item?.arrival?.time}
                                </span>
                              </p>
                              <p className="fs-8">
                                Terminal- {item?.departure?.terminal}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {item?.type === "hotel" && (
                        <div className="pin-container">
                          <div className="d-flex align-items-start pe-2">
                            <div style={{ width: "15%", height: "15%" }}>
                              <img
                                src={item?.heroImage}
                                width="100%"
                                alt=""
                                style={{ objectFit: "contain" }}
                                className="border-radius-6px"
                              />
                            </div>
                            <div className="w100 ms-3">
                              <h3>{item.name}</h3>
                              <div className="d-flex align-items-center justify-content-between df-wrap">
                                <p className="fs-8">
                                  {item?.roomsAndRates?.name}
                                </p>
                                <div className="d-flex align-items-center">
                                  <p className="fs-8 ms-2">
                                    {format(
                                      new Date(item?.checkin),
                                      "EEE, dd MMM"
                                    )}
                                  </p>
                                  <p>-</p>
                                  <p className="fs-8 ms-2">
                                    {" "}
                                    {format(
                                      new Date(item?.checkout),
                                      "EEE, dd MMM"
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {item?.type === "pin" && (
                        <div className="pin-container">
                          <div className="d-flex align-items-start pe-2">
                            <div style={{ width: "15%", height: "auto" }}>
                              <img
                                src={item?.image}
                                width="100%"
                                alt=""
                                className="border-radius-6px"
                              />
                            </div>
                            <div className="w100 ms-3">
                              <h3>{item.name}</h3>
                              <p className="fs-8">{item?.address}</p>
                            </div>
                            <div
                              className="d-flex align-items-center justify-content-center"
                              style={{ gap: 5 }}
                            >
                              <p className="mt-1 fw-700">{item?.rating}</p>
                              <StarIcon
                                style={{ color: "var(--yellow-color)" }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div
            className="col-md-6 mt-3 position-fixed"
            style={{ right: 0, height: "88vh", top: "12vh" }}
          >
            <DirectionsMapComp
              tripId={selectedTrip?._id}
              markerData={filteredPins() || []}
              disablePinsMarking={true}
              zoom={17}
              showDirections={true}
              styles={{ height: "88vh" }}
            />
          </div>
        </div>
      </Modal>
      <Modal open={openModel} onClose={() => setOpenModel(false)}>
        <div className="t-center">
          <h4 className="text-center mb-4">
            Sign up or log in to add your trip.
          </h4>
          <div>
            <button
              className="btn-contained"
              onClick={() => navigate("/signup")}
            >
              Signup/Login
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TripModal;
